import React from 'react';
import {
    isMoveToManagementRouterEnabled,
    isHideVsaEndpointsEnabled,
    isVsaAdoptionHeaderChatButtonEnabled,
} from 'feature-flags';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useLazyLoadQuery, graphql } from 'react-relay';
import { usePathParam } from 'react-resource-router';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import AiIcon from '@atlaskit/icon/core/ai-agent';
import { UI_EVENT_TYPE, ContextualAnalyticsData, SCREEN } from '@atlassian/analytics-bridge';
import { HelpHelpCenterAri } from '@atlassian/ari';
import { HELP_CENTERS_MANAGEMENT_BASE_PATH, ScreenName } from '@atlassian/help-center-common-component/constants';

import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { history } from '@atlassian/help-center-common-util/history';
import { getHelpCenterAri, getCloudId } from '@atlassian/help-center-common-util/meta';
import { useVirtualAgentAvailability } from '@atlassian/help-center-virtual-agent/src/services/virtual-agent-availability';
import { DynamicThemingButton } from '../dynamic-theming-button';
import type { virtualServiceAgentHeaderDumbQuery } from './__generated__/virtualServiceAgentHeaderDumbQuery.graphql';
import messages from './messages';

export interface Props {
    isJiraAdmin: boolean;
    projectId?: number;
}

const VirtualServiceAgentButton = ({ onClick, label }: { onClick: () => void; label: string }) => {
    const { formatMessage } = useIntl();
    return (
        <ContextualAnalyticsData sourceName={ScreenName.VIRTUAL_SERVICE_AGENT_HEADER} sourceType={SCREEN}>
            <DynamicThemingButton
                label={label}
                icon={<AiIcon label={formatMessage(messages.virtualServiceAgent)} />}
                onClick={onClick}
            />
            <FireScreenEventOnMount />
        </ContextualAnalyticsData>
    );
};
const VirtualServiceAgentHeaderForPortal = ({
    cloudId,
    portalId,
    projectId,
}: {
    cloudId: string;
    portalId: string;
    projectId: number;
}): React.ReactNode => {
    di(isVsaAdoptionHeaderChatButtonEnabled);
    di(useVirtualAgentAvailability);
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { formatMessage } = useIntl();
    const { isVirtualAgentEnabled: isVirtualAgentAvailableForProject } = useVirtualAgentAvailability({
        cloudId,
        projectId,
        isLoggedIn: true, // this component has a check to only show for logged in users
    });
    const handleVirtualAgentMenuClick = () => {
        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'clicked',
            actionSubjectId: 'virtualServiceAgentChatButton',
            attributes: {
                source: 'portal',
            },
        }).fire();
        history.push(`/portal/${portalId}/conversation`);
    };
    if (isVirtualAgentAvailableForProject && isVsaAdoptionHeaderChatButtonEnabled()) {
        return (
            <ContextualAnalyticsData sourceName={ScreenName.VIRTUAL_SERVICE_AGENT_HEADER} sourceType={SCREEN}>
                <VirtualServiceAgentButton onClick={handleVirtualAgentMenuClick} label={formatMessage(messages.chat)} />
                <FireScreenEventOnMount />
            </ContextualAnalyticsData>
        );
    }
    return null;
};

const VirtualServiceAgentHeaderForHelpCenter = ({ cloudId }: { cloudId: string }) => {
    di(isVsaAdoptionHeaderChatButtonEnabled);
    const helpCenterAri = getHelpCenterAri();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { formatMessage } = useIntl();
    const virtualAgentEnabledData = useLazyLoadQuery<virtualServiceAgentHeaderDumbQuery>(
        graphql`
            query virtualServiceAgentHeaderDumbHelpCenterQuery($cloudId: ID!, $helpCenterAri: ID!) {
                helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
                    helpCenterById(helpCenterAri: $helpCenterAri) {
                        __typename
                        ... on HelpCenter {
                            virtualAgentEnabled
                        }
                    }
                }
                virtualAgent {
                    virtualAgentAvailability(containerId: $helpCenterAri)
                }
            }
        `,
        {
            cloudId,
            helpCenterAri,
        }
    );
    const isVirtualAgentEnabled =
        virtualAgentEnabledData?.helpCenter?.helpCenterById?.__typename === 'HelpCenter' &&
        virtualAgentEnabledData.helpCenter.helpCenterById.virtualAgentEnabled;

    const isVirtualAgentAvailable = virtualAgentEnabledData?.virtualAgent?.virtualAgentAvailability ?? false;
    const isHelpSeekerWithVSAEnabled =
        isVsaAdoptionHeaderChatButtonEnabled() && isVirtualAgentAvailable && isVirtualAgentEnabled;
    const handleVirtualAgentMenuClick = () => {
        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'clicked',
            actionSubjectId: 'virtualServiceAgentChatButton',
            attributes: {
                source: 'help-center',
            },
        }).fire();
        history.push(`/conversation`);
    };

    if (isHelpSeekerWithVSAEnabled) {
        return (
            <ContextualAnalyticsData sourceName={ScreenName.VIRTUAL_SERVICE_AGENT_HEADER} sourceType={SCREEN}>
                <VirtualServiceAgentButton onClick={handleVirtualAgentMenuClick} label={formatMessage(messages.chat)} />
                <FireScreenEventOnMount />
            </ContextualAnalyticsData>
        );
    }
    return null;
};

const VirtualServiceAgentHeaderForAdmin = ({ cloudId, isJiraAdmin }: { cloudId: string; isJiraAdmin: boolean }) => {
    const helpCenterAri = getHelpCenterAri();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { formatMessage } = useIntl();
    const virtualAgentEnabledData = useLazyLoadQuery<virtualServiceAgentHeaderDumbQuery>(
        graphql`
            query virtualServiceAgentHeaderDumbAdminQuery($cloudId: ID!, $helpCenterAri: ID!) {
                helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
                    helpCenterById(helpCenterAri: $helpCenterAri) {
                        __typename
                        ... on HelpCenter {
                            virtualAgentEnabled
                        }
                    }
                }
            }
        `,
        {
            cloudId,
            helpCenterAri,
        }
    );
    const isVirtualAgentEnabled =
        virtualAgentEnabledData?.helpCenter?.helpCenterById?.__typename === 'HelpCenter' &&
        virtualAgentEnabledData.helpCenter.helpCenterById.virtualAgentEnabled;
    const adminWithoutVSAEnabled = isJiraAdmin && !isVirtualAgentEnabled;
    const handleVirtualAgentMenuClick = () => {
        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'clicked',
            actionSubjectId: 'virtualServiceAgentButton',
            attributes: {
                source: 'admin',
            },
        }).fire();
        if (isMoveToManagementRouterEnabled()) {
            const { helpCenterId } = HelpHelpCenterAri.parse(helpCenterAri);
            window.location.href = `${window.location.origin}${HELP_CENTERS_MANAGEMENT_BASE_PATH}/${helpCenterId}/virtual-agent`;
        } else {
            history.push('/virtual-agent');
        }
    };
    if (adminWithoutVSAEnabled) {
        return (
            <ContextualAnalyticsData sourceName={ScreenName.VIRTUAL_SERVICE_AGENT_HEADER} sourceType={SCREEN}>
                <VirtualServiceAgentButton
                    onClick={handleVirtualAgentMenuClick}
                    label={formatMessage(messages.virtualServiceAgent)}
                />
                <FireScreenEventOnMount />
            </ContextualAnalyticsData>
        );
    }
    return null;
};

const VirtualServiceAgentHeaderDumbWrapper = ({ isJiraAdmin, projectId }: Props) => {
    di(getCloudId);
    di(isHideVsaEndpointsEnabled);
    di(usePathParam);
    const cloudId = getCloudId();
    const [portalId] = usePathParam('portalId');
    if (isHideVsaEndpointsEnabled()) {
        return null;
    }
    if (isJiraAdmin) {
        return <VirtualServiceAgentHeaderForAdmin cloudId={cloudId} isJiraAdmin />;
    }
    // at this point all the users are help seekers and are logged in
    // we have no special checks for project admins, they continue to see the button in the header just like help seekers would
    if (portalId) {
        // this is different that portalId && projectId because we don't want to render the
        // button for a help seeker in portal if projectId is not present
        // if we do && it would get into the the help center header
        // we want it to explicitly render null if projectId is not present but portalId is present
        // better to not render anything in this case than to render the button that redirects to the help center conversation page
        if (projectId) {
            return <VirtualServiceAgentHeaderForPortal cloudId={cloudId} portalId={portalId} projectId={projectId} />;
        }
        return null;
    }
    return <VirtualServiceAgentHeaderForHelpCenter cloudId={cloudId} />;
};
export default VirtualServiceAgentHeaderDumbWrapper;
